import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Search } from 'lucide-react'
import { api } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { formatDate } from '../lib/format'
import { atLeast, getSession } from '../lib/auth'
import { Badge, EmptyState, Panel, Skeleton } from '../components/primitives'
import type { Priority, Task, TaskStatus, TaskType } from '../lib/types'

const TYPES: TaskType[] = ['Bug', 'Feature', 'TechnicalTask', 'Refactoring', 'Documentation', 'Research', 'Other']
const PRIORITIES: Priority[] = ['Low', 'Medium', 'High', 'Critical']

/**
 * The statuses a person may set by hand.
 *
 * Deliberately not the whole enum. `InProgress` is what a work session means, and letting someone
 * type it here would put the task in a state no agent is backing — the board would claim work is
 * happening that nothing is doing. Starting work is the agent's job; this list is the states a
 * human owns.
 */
const EDITABLE_STATUSES: TaskStatus[] = ['Backlog', 'Waiting', 'Assigned', 'ReadyForReview', 'Done', 'Cancelled']

export default function Tasks() {
  const { t, locale } = useI18n()
  const queryClient = useQueryClient()
  const session = getSession()
  const canCreate = atLeast(session?.role, 'Developer')

  // "/tasks?search=KNIT-12" — how a document links to the task it is about.
  const [params] = useSearchParams()
  const [search, setSearch] = useState(() => params.get('search') ?? '')
  const [statusFilter, setStatusFilter] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<Task | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['tasks', { search, statusFilter, typeFilter }],
    queryFn: () =>
      api.tasks.list({
        search: search || undefined,
        status: statusFilter || undefined,
        type: typeFilter || undefined,
        pageSize: 100,
      }),
  })

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.tasks.create(body),
    onSuccess: () => {
      setCreating(false)
      void queryClient.invalidateQueries({ queryKey: ['tasks'] })
    },
  })

  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) => api.tasks.update(id, body),
    onSuccess: () => {
      setEditing(null)
      void queryClient.invalidateQueries({ queryKey: ['tasks'] })
      // A task's status and assignee are what the bug hunt draws, so it has to hear about this.
      void queryClient.invalidateQueries({ queryKey: ['bug-hunt'] })
    },
  })

  const tasks = data?.items ?? []

  return (
    <div className="stack">
      <div className="row row--wrap">
        <label className="row row--tight" style={{ minWidth: 240 }}>
          <Search size={14} className="muted" />
          <input
            className="input"
            placeholder={t('tasks.searchPlaceholder')}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>

        <select className="select" style={{ width: 'auto' }} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          <option value="">{t('tasks.type')}: {t('common.all')}</option>
          {TYPES.map((type) => (
            <option key={type} value={type}>{t(`taskType.${type}`)}</option>
          ))}
        </select>

        <select className="select" style={{ width: 'auto' }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">{t('tasks.status')}: {t('common.all')}</option>
          {['Waiting', 'Assigned', 'InProgress', 'ReadyForReview', 'Done', 'Reopened'].map((status) => (
            <option key={status} value={status}>{t(`taskStatus.${status}`)}</option>
          ))}
        </select>

        <div className="spacer" />

        {canCreate && (
          <button type="button" className="btn btn--primary" onClick={() => setCreating((open) => !open)}>
            <Plus size={14} />
            {t('tasks.new')}
          </button>
        )}
      </div>

      {creating && (
        <Panel title={t('tasks.new')}>
          <TaskForm
            onSubmit={(body) => create.mutate(body)}
            onCancel={() => setCreating(false)}
            busy={create.isPending}
            error={create.error instanceof Error ? create.error.message : null}
          />
        </Panel>
      )}

      {editing && (
        <Panel title={`${t('tasks.edit')} · ${editing.externalId}`}>
          <TaskForm
            task={editing}
            onSubmit={(body) => update.mutate({ id: editing.id, body })}
            onCancel={() => setEditing(null)}
            busy={update.isPending}
            error={update.error instanceof Error ? update.error.message : null}
          />
        </Panel>
      )}

      <Panel flush>
        {isLoading ? (
          <div className="stack" style={{ padding: 'var(--s4)' }}>
            <Skeleton height={36} />
            <Skeleton height={36} />
            <Skeleton height={36} />
          </div>
        ) : tasks.length === 0 ? (
          <EmptyState title={t('tasks.empty')} body={t('tasks.emptyHint')} />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>{t('tasks.externalId')}</th>
                <th>{t('tasks.taskTitle')}</th>
                <th>{t('tasks.type')}</th>
                <th>{t('tasks.status')}</th>
                <th>{t('tasks.priority')}</th>
                <th>{t('tasks.assignee')}</th>
                <th>{t('tasks.created')}</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((task) => (
                // The whole row opens the editor, which is what a table of records is expected to
                // do. Without it the list was a dead end: everything visible, nothing changeable.
                <tr
                  key={task.id}
                  onClick={canCreate ? () => { setCreating(false); setEditing(task) } : undefined}
                  className={canCreate ? 'table__row--clickable' : undefined}
                  aria-label={canCreate ? `${t('tasks.edit')} ${task.externalId}` : undefined}
                >
                  <td className="mono small bold nowrap">{task.externalId}</td>
                  <td className="truncate" style={{ maxWidth: 320 }}>{task.title}</td>
                  <td><Badge>{t(`taskType.${task.type}`)}</Badge></td>
                  <td>
                    <Badge tone={task.status === 'Done' ? 'ok' : task.status === 'ReadyForReview' ? 'warn' : task.status === 'InProgress' ? 'accent' : 'neutral'}>
                      {t(`taskStatus.${task.status}`)}
                    </Badge>
                  </td>
                  <td>
                    <Badge tone={task.priority === 'Critical' ? 'danger' : task.priority === 'High' ? 'warn' : 'neutral'}>
                      {t(`priority.${task.priority}`)}
                    </Badge>
                  </td>
                  <td className="small">{task.assignedDeveloperName ?? <span className="muted">{t('common.unassigned')}</span>}</td>
                  <td className="tiny muted nowrap">{formatDate(task.createdAt, locale)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </div>
  )
}

/** Creates a task, or edits one. The same fields either way, plus a status when editing. */
function TaskForm({
  task,
  onSubmit,
  onCancel,
  busy,
  error,
}: {
  task?: Task
  onSubmit: (body: Record<string, unknown>) => void
  onCancel: () => void
  busy: boolean
  error: string | null
}) {
  const { t } = useI18n()
  const [externalId, setExternalId] = useState(task?.externalId ?? '')
  const [title, setTitle] = useState(task?.title ?? '')
  const [description, setDescription] = useState(task?.description ?? '')
  const [type, setType] = useState<TaskType>(task?.type ?? 'Bug')
  const [priority, setPriority] = useState<Priority>(task?.priority ?? 'Medium')
  const [status, setStatus] = useState<TaskStatus>(task?.status ?? 'Backlog')

  const { data: developers } = useQuery({ queryKey: ['developers'], queryFn: api.settings.developers })
  const [assignedDeveloperId, setAssignedDeveloperId] = useState(task?.assignedDeveloperId ?? '')

  return (
    <form
      className="stack"
      onSubmit={(event) => {
        event.preventDefault()
        onSubmit({
          externalId,
          title,
          description: description || undefined,
          type,
          priority,
          assignedDeveloperId: assignedDeveloperId || undefined,
          ...(task ? { status } : {}),
        })
      }}
    >
      <div className="grid" style={{ gridTemplateColumns: '160px 1fr' }}>
        <label className="field">
          <span className="field__label">{t('tasks.externalId')}</span>
          {/*
            Read-only once it exists. This is the identity an agent references from outside — an
            MCP call says "KNIT-2841" — so renaming it here would quietly break every agent
            configured against the old one.
          */}
          <input
            className="input mono"
            value={externalId}
            onChange={(e) => setExternalId(e.target.value)}
            required
            placeholder="KNIT-2841"
            readOnly={Boolean(task)}
            disabled={Boolean(task)}
          />
        </label>
        <label className="field">
          <span className="field__label">{t('tasks.taskTitle')}</span>
          <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} required />
        </label>
      </div>

      <label className="field">
        <span className="field__label">{t('tasks.description')} <span className="muted">({t('common.optional')})</span></span>
        <textarea className="textarea" value={description} onChange={(e) => setDescription(e.target.value)} />
      </label>

      <div className="grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <label className="field">
          <span className="field__label">{t('tasks.type')}</span>
          <select className="select" value={type} onChange={(e) => setType(e.target.value as TaskType)}>
            {TYPES.map((value) => <option key={value} value={value}>{t(`taskType.${value}`)}</option>)}
          </select>
        </label>

        <label className="field">
          <span className="field__label">{t('tasks.priority')}</span>
          <select className="select" value={priority} onChange={(e) => setPriority(e.target.value as Priority)}>
            {PRIORITIES.map((value) => <option key={value} value={value}>{t(`priority.${value}`)}</option>)}
          </select>
        </label>

        <label className="field">
          <span className="field__label">{t('tasks.assignee')}</span>
          <select className="select" value={assignedDeveloperId} onChange={(e) => setAssignedDeveloperId(e.target.value)}>
            <option value="">{t('common.unassigned')}</option>
            {(developers ?? []).map((developer) => (
              <option key={developer.id} value={developer.id}>{developer.displayName}</option>
            ))}
          </select>
        </label>
      </div>

      {task && (
        <label className="field" style={{ maxWidth: 260 }}>
          <span className="field__label">{t('tasks.status')}</span>
          <select className="select" value={status} onChange={(e) => setStatus(e.target.value as TaskStatus)}>
            {EDITABLE_STATUSES.map((value) => (
              <option key={value} value={value}>{t(`taskStatus.${value}`)}</option>
            ))}
          </select>
        </label>
      )}

      {error && <div className="field__error">{error}</div>}

      <div className="row">
        <button type="submit" className="btn btn--primary" disabled={busy}>
          {busy ? t('common.saving') : task ? t('common.save') : t('common.create')}
        </button>
        <button type="button" className="btn" onClick={onCancel}>{t('common.cancel')}</button>
      </div>
    </form>
  )
}
